import { AdviseCurrentSuppliers, AdviseEngagement } from '../../../types/advise';
import { fetchAdviseEngagementById } from '../../../Api/Advise/Engagements';
import { CallResult } from '../../../Api/callExternalApi';
import { isNotFoundRefusal, refusalMessage } from '../../../utils/refusalMessage';
import { BYTES_PER_MB } from '../../../utils/formatFileSize';

export const CHANGE_FAILED = "Couldn't save the change to this supplier. Please try again.";
const STALE_LINES = "These line items changed since they were loaded. They've been reloaded.";
const STALE_UNREAD = "These line items changed since they were loaded and couldn't be reloaded. Refresh the page.";

export const NO_SUPPLIERS: AdviseCurrentSuppliers = { skipped: false, suppliers: [] };

export const findSupplier = (current: AdviseCurrentSuppliers, supplierId: string) =>
  current.suppliers?.find((supplier) => supplier.id === supplierId);

type Sent = CallResult<AdviseEngagement> & { now: AdviseCurrentSuppliers | null };
type ApiError = CallResult<AdviseEngagement>['error'];
// What a POST adds, so a re-read can tell whether one that got no answer saved anyway.
export type AddedBy = (before: AdviseCurrentSuppliers, now: AdviseCurrentSuppliers) => boolean;
export type Landing = { landed: (now: AdviseCurrentSuppliers) => boolean; pauseMs: number };

export const REREAD_PAUSE_MS = 2000;
// upstack_api writes the file to S3 before it saves the invoice, so a bigger upload takes longer to land.
export const uploadPauseMs = (file: File) => REREAD_PAUSE_MS + Math.round((file.size / BYTES_PER_MB) * 1000);

export const statusOf = (error: ApiError) => error?.errorResponse?.response?.status;
// A gateway that gave up answers for the app, which may still finish the save behind it.
const GATEWAY_GAVE_UP = [502, 503, 504];
// Sent but never answered; an error without an axios one, such as a missing token, never left the browser.
export const isUnanswered = (error: ApiError) => {
  if (!error?.errorResponse) return false;
  const status = statusOf(error);
  return !status || GATEWAY_GAVE_UP.includes(status);
};
// A 409 answers a stale line-items version, so only a re-read shows what a teammate saved meanwhile.
const needsReread = (error: ApiError) => isNotFoundRefusal(error) || [409, 422].includes(statusOf(error) ?? 0);

export const changeRefusal = ({ error, now }: Sent) => {
  if (statusOf(error) !== 409) return refusalMessage(error, CHANGE_FAILED, CHANGE_FAILED);
  return now ? STALE_LINES : STALE_UNREAD;
};

const pause = (ms: number) => new Promise((resume) => setTimeout(resume, ms));

const readStep = async (id: string) => {
  const { data } = await fetchAdviseEngagementById(id);
  return data ? (data.current_suppliers ?? NO_SUPPLIERS) : null;
};

// Only what the step holds is taken, so edits other tabs are still saving stay on screen. A POST with no answer may
// still be saving on the server, so a re-read that doesn't show it yet is tried once more before it counts as failed.
export const withRereadAfterFailure = async (
  id: string,
  result: CallResult<AdviseEngagement>,
  landing?: Landing
): Promise<Sent> => {
  const unanswered = !!landing && isUnanswered(result.error);
  if (!needsReread(result.error) && !unanswered) return { ...result, now: null };
  const now = await readStep(id);
  if (!landing || !unanswered || (now && landing.landed(now))) return { ...result, now };
  await pause(landing.pauseMs);
  return { ...result, now: (await readStep(id)) ?? now };
};

// Any new invoice counts, since the API stores the file name as Rack decodes it, not as the browser sent it. So a
// teammate's upload to the same supplier while ours went unanswered is taken for ours: a rare race, accepted.
export const invoiceAdded =
  (supplierId: string): AddedBy =>
  (before, now) => {
    const known = new Set(findSupplier(before, supplierId)?.invoices.map((invoice) => invoice.id));
    return !!findSupplier(now, supplierId)?.invoices.some((invoice) => !known.has(invoice.id));
  };
